import type { RouteConfig } from 'components/Navigation/SubNavigation/types';
import type { TeamPageProps } from './types';

import { Redirect, Route, Switch } from 'react-router-dom';
import { settingsUrlBase, settingsPagePaths } from 'pages/Settings/paths';
import { KizenError } from 'services/errors';
import { PermissionGroupsPage } from './PermissionGroupsPage';
import { RolesPage } from './RolesPage';
import TeamMemberPage from './TeamMemberPage';
import { StyledSubNavigation } from './styles';
import { SubNavWrapper } from '../../styled';

type TFn = (x: string) => string;

const TEAM_ROUTE = `${settingsUrlBase}/${settingsPagePaths.team}`;
const ROLES_ROUTE = `${TEAM_ROUTE}/roles`;
const PERMISSION_GROUPS_ROUTE = `${TEAM_ROUTE}/permission-groups`;

const routes = [
  {
    path: TEAM_ROUTE,
    label: (t: TFn) => t('Team'),
    component: TeamMemberPage,
    exact: true,
  },
  {
    path: ROLES_ROUTE,
    label: (t: TFn) => t('Roles'),
    component: RolesPage,
    exact: true,
  },
  {
    path: PERMISSION_GROUPS_ROUTE,
    label: (t: TFn) => t('Permission Groups'),
    component: PermissionGroupsPage,
    exact: true,
  },
];

export default (props: TeamPageProps) => {
  const { access, location } = props;

  const canViewTeam = access.teamMembers.view;
  const canViewRoles = access.roles.view;
  const canViewPermissionGroup = access.permissionGroups.view;

  if (location.pathname === TEAM_ROUTE && !canViewTeam) {
    return <Redirect exact to={ROLES_ROUTE} />;
  }

  if (location.pathname === ROLES_ROUTE && !canViewRoles) {
    return <Redirect exact to={PERMISSION_GROUPS_ROUTE} />;
  }

  if (
    location.pathname === PERMISSION_GROUPS_ROUTE &&
    !canViewPermissionGroup
  ) {
    throw new KizenError(403);
  }

  const visibleRoutes = routes.reduce<Record<string, RouteConfig>>(
    (acc, route) => {
      if (route.path === TEAM_ROUTE && !canViewTeam) return acc;
      if (route.path === ROLES_ROUTE && !canViewRoles) return acc;
      if (route.path === PERMISSION_GROUPS_ROUTE && !canViewPermissionGroup)
        return acc;

      acc[route.path] = route;
      return acc;
    },
    {}
  );

  return (
    <>
      <SubNavWrapper>
        <StyledSubNavigation
          small
          routes={visibleRoutes}
          currentSearch={location.search}
        />
      </SubNavWrapper>
      <Switch>
        {Object.entries(visibleRoutes).map(([key, route]) => {
          if (route.component) {
            const Component = route.component;

            return (
              <Route
                key={key}
                path={route.path}
                exact={route.exact !== false}
                render={() => <Component {...props} />}
              />
            );
          }
        })}
      </Switch>
    </>
  );
};
